export const FRONTEND_CONFIG_OVERRIDE_COOKIE_PREFIX = 'fc_override_';
export const FRONTEND_CONFIG_OVERRIDE_MAX_AGE_SECONDS = 86400;

export type FrontendConfigType = 'Boolean' | 'String' | 'Number' | 'StringCsv' | 'NumberCsv';

export type FrontendConfigValue = boolean | string | number | Array<string> | Array<number>;

export type FrontendConfigOverride = {
  type: FrontendConfigType;
  name: string;
  value: FrontendConfigValue;
};

const COOKIE_TYPE_NAMES: Record<FrontendConfigType, string> = {
  Boolean: 'boolean',
  String: 'string',
  Number: 'number',
  StringCsv: 'string-csv',
  NumberCsv: 'number-csv',
};

const CONFIG_TYPES: Array<FrontendConfigType> = ['Boolean', 'StringCsv', 'NumberCsv', 'String', 'Number'];

export function getFrontendConfigOverrideCookieName(type: FrontendConfigType, name: string): string {
  return `${FRONTEND_CONFIG_OVERRIDE_COOKIE_PREFIX}${COOKIE_TYPE_NAMES[type]}_${name}`;
}

export function serializeFrontendConfigOverride(_type: FrontendConfigType, value: FrontendConfigValue): string {
  return encodeURIComponent(JSON.stringify(value));
}

function parseOverride(encodedValue: string): unknown {
  try {
    return JSON.parse(decodeURIComponent(encodedValue)) as unknown;
  } catch {
    return undefined;
  }
}

function parseBooleanOverride(encodedValue: string): boolean | undefined {
  const value = parseOverride(encodedValue);
  return typeof value === 'boolean' ? value : undefined;
}

function parseStringOverride(encodedValue: string): string | undefined {
  const value = parseOverride(encodedValue);
  return typeof value === 'string' ? value : undefined;
}

function parseNumberOverride(encodedValue: string): number | undefined {
  const value = parseOverride(encodedValue);
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function parseStringListOverride(encodedValue: string): Array<string> | undefined {
  const list = parseOverride(encodedValue);
  return Array.isArray(list) && list.every((item) => typeof item === 'string') ? list : undefined;
}

function parseNumberListOverride(encodedValue: string): Array<number> | undefined {
  const list = parseOverride(encodedValue);
  return Array.isArray(list) && list.every((item) => typeof item === 'number' && Number.isFinite(item))
    ? list
    : undefined;
}

export function parseFrontendConfigOverride(
  type: FrontendConfigType,
  encodedValue: string,
): FrontendConfigValue | undefined {
  switch (type) {
    case 'Boolean':
      return parseBooleanOverride(encodedValue);
    case 'String':
      return parseStringOverride(encodedValue);
    case 'Number':
      return parseNumberOverride(encodedValue);
    case 'StringCsv':
      return parseStringListOverride(encodedValue);
    case 'NumberCsv':
      return parseNumberListOverride(encodedValue);
  }
}

export function parseFrontendConfigOverrideCookie(
  cookieName: string,
  cookieValue: string,
): FrontendConfigOverride | undefined {
  for (const type of CONFIG_TYPES) {
    const prefix = getFrontendConfigOverrideCookieName(type, '');
    if (cookieName.startsWith(prefix)) {
      const name = cookieName.slice(prefix.length);
      const value = parseFrontendConfigOverride(type, cookieValue);
      return value === undefined ? undefined : { type, name, value };
    }
  }

  if (cookieName.startsWith(FRONTEND_CONFIG_OVERRIDE_COOKIE_PREFIX)) {
    const value = parseBooleanOverride(cookieValue);
    return value === undefined
      ? undefined
      : {
          type: 'Boolean',
          name: cookieName.slice(FRONTEND_CONFIG_OVERRIDE_COOKIE_PREFIX.length),
          value,
        };
  }

  return undefined;
}

function getCookieValue(name: string): string | undefined {
  if (typeof document === 'undefined') {
    return undefined;
  }

  const cookie = document.cookie.split(';').find((item) => item.trim().startsWith(`${name}=`));
  return cookie?.trim().slice(name.length + 1);
}

export function getBooleanOverride(name: string): boolean | undefined {
  const typedValue = getCookieValue(getFrontendConfigOverrideCookieName('Boolean', name));
  if (typedValue !== undefined) {
    const override = parseBooleanOverride(typedValue);
    if (override !== undefined) {
      return override;
    }
  }

  const legacyValue = getCookieValue(`${FRONTEND_CONFIG_OVERRIDE_COOKIE_PREFIX}${name}`);
  return legacyValue === undefined ? undefined : parseBooleanOverride(legacyValue);
}

export function getStringOverride(name: string): string | undefined {
  const value = getCookieValue(getFrontendConfigOverrideCookieName('String', name));
  return value === undefined ? undefined : parseStringOverride(value);
}

export function getNumberOverride(name: string): number | undefined {
  const value = getCookieValue(getFrontendConfigOverrideCookieName('Number', name));
  return value === undefined ? undefined : parseNumberOverride(value);
}

export function getStringListOverride(name: string): Array<string> | undefined {
  const value = getCookieValue(getFrontendConfigOverrideCookieName('StringCsv', name));
  return value === undefined ? undefined : parseStringListOverride(value);
}

export function getNumberListOverride(name: string): Array<number> | undefined {
  const value = getCookieValue(getFrontendConfigOverrideCookieName('NumberCsv', name));
  return value === undefined ? undefined : parseNumberListOverride(value);
}
